import {ProfileRole, ProfileType} from './profile';

export interface AuthRequest {
    login: string;
    password: string;
    saveCredentials: boolean;
}

export interface LegalPerson {
    id?: number;
    ein?: string;
    tin: string;
    gln?: string;
    gcp?: string;
    gps?: string;
    country: string;
    vat_id?: string;
    company_name: string;
    short_name: string;
    legal_address: string;
    postal_code?: string;
    city?: string;
    street?: string;
    iban: string;
    bic: string;
    taxAuthName: string;
    taxAuthCode: string;
    types?: Array<ProfileRole>;
}

export interface TaxAuthorityJurisdiction {
    id: string;
    name: string;
}

export interface ProfileRaw {
    username: string;
    email: string;
    emailVerified: boolean;
    firstName: string;
    lastName: string;
    fullName: string;
    authorities: Array<string>;
    roles: Array<UserRoles>;
    signEds: boolean;
    clientId: number;
    phone?: string;
    client?: {
        businessPlaceCode: string | null;
        storageType: string | null;
        storageGln: string | null;
        gcp: string | null;
        legalIdentityId: number | null;
        manufacturerId: number | null;
        id: number;
        type: ProfileRole;
        role: ProfileType;
        fullName: string;
        shortName: string;
        email: string | null;
        ceo: string | null;
        legalAddress: string;
        participantId: string | null;
        legalIdentity: string | null;
        legalName: string | null;
    };
    legalPerson?: LegalPerson;
}

export interface TokensDataRaw {
    accessToken: string;
    refreshToken: string;
    accessExpiresAt: string;
    refreshExpiresAt: string;
}

export interface AuthDataRaw extends TokensDataRaw {
    profile: ProfileRaw;
}

export interface Profile {
    username: string;
    email: string;
    emailVerified: boolean;
    firstName: string;
    lastName: string;
    fullName: string;
    permissions: Array<UserPermissionValue>;
    roles: Array<UserRoles>;
    signEds: boolean;
    clientId: number;
    phone?: string;
    client?: {
        businessPlaceCode: string | null;
        storageType: string | null;
        storageGln: string | null;
        gcp: string | null;
        legalIdentityId: number | null;
        manufacturerId: number | null;
        id: number;
        type: ProfileRole;
        role: ProfileType;
        fullName: string;
        shortName: string;
        email: string | null;
        ceo: string | null;
        legalAddress: string;
        participantId: string | null;
        legalIdentity: string | null;
        legalName: string | null;
    };
    legalPerson?: LegalPerson;
}

export interface CredentialsFIRS {
    businessId: string;
    apiKey: string;
    apiSecret: string;
    serviceId: string;
    publicKey: string;
    certificate: string;
}

export interface TokensData {
    accessToken: string;
    refreshToken: string;
    accessTokenExpiresIn: Date;
    refreshTokenExpiresIn: Date;
}

export interface AuthData extends TokensData {
    profile: Profile;
}

export enum UserPermission {
    // Complaints
    APPEAL_ALL_LIST = 'PERMISSION_MPL_APPEAL_ALL_LIST',
    APPEAL_LIST = 'PERMISSION_MPL_APPEAL_LIST',
    APPEAL_CREATE = 'PERMISSION_MPL_APPEAL_CREATE',
    PERSONNEL_NK = 'PERMISSION_MPL_PERSONNEL_NK',
    NK_BRANCH_MANAGE = 'PERMISSION_MPL_NK_BRANCH_MANAGE',

    // Organizations
    ORGANIZATION_EDIT = 'PERMISSION_MPL_ISEMT_ORGANIZATION_EDIT',

    // Profile
    PROFILE_DATA_VIEW = 'PERMISSION_MPL_PROFILE_DATA_VIEW',
    PROFILE_DATA_EDITING = 'PERMISSION_MPL_PROFILE_DATA_EDITING',
    FACTORY_CONTROL_CREATE = 'PERMISSION_MPL_FACTORY_CONTROL_CREATE',
    FACTORY_GRID_VIEW = 'PERMISSION_MPL_FACTORY_GRID_VIEW',
    FACTORY_CARD_VIEW = 'PERMISSION_MPL_FACTORY_CARD_VIEW',
    FACTORY_LINE_CONTROL_CREATE = 'PERMISSION_MPL_FACTORY_LINE_CONTROL_CREATE',
    FACTORY_CONTROL_EDIT = 'PERMISSION_MPL_FACTORY_CONTROL_EDIT',
    FACTORY_LINE_CONTROL_EDIT = 'PERMISSION_MPL_FACTORY_LINE_CONTROL_EDIT',
    FACTORY_BILLING_VIEW = 'PERMISSION_MPL_FACTORY_BILLING_VIEW',
    ROLE_TYPE_EDIT = 'PERMISSION_MPL_ISEMT_ROLE_EDIT',
    ROLE_TYPE_VIEW = 'PERMISSION_MPL_ISEMT_ROLE_VIEW',

    // Products
    PRODUCT_GRID_VIEW = 'PERMISSION_MPL_PRODUCT_GRID_VIEW',
    ALL_PRODUCT_GRID_VIEW = 'PERMISSION_MPL_ISEMT_PRODUCT_REGISTRY_VIEW',
    PRODUCT_DETAIL_VIEW = 'PERMISSION_MPL_ISEMT_MC_HISTORY_VIEW',
    PRODUCT_CARD_VIEW = 'PERMISSION_MPL_PRODUCT_CARD_VIEW',
    PRODUCT_CARD_ALL_VIEW = 'PERMISSION_MPL_ISEMT_PRODUCT_CARD_ALL_VIEW',

    //Receipts
    RECEIPT_CREATE = 'PERMISSION_MPL_RECEIPT_CONTROL_CREATE',
    RECEIPT_GRID_VIEW = 'PERMISSION_MPL_RECEIPT_GRID_VIEW',
    RECEIPT_CARD_VIEW = 'PERMISSION_MPL_RECEIPT_CARD_VIEW',

    // Orders
    ORDER_CREATE_CUSTOMER = 'PERMISSION_MPL_CONSUMER_CODES',
    ORDER_CREATE_TRANSPORT = 'PERMISSION_MPL_TRANSPORT_CODES',
    ORDER_CREATE_CUSTOMS = 'PERMISSION_MPL_CUSTOMS_CODES',

    // Documents
    DOCUMENT_GRID_VIEW = 'PERMISSION_MPL_DOC_GRID_VIEW',
    DOCUMENT_DETAIL_VIEW = 'PERMISSION_MPL_DOC_CARD_VIEW',

    DOCUMENT_CREATE_UTILISATION = 'PERMISSION_MPL_ISEMT_DOC_UTILISATION_CREATE',
    DOCUMENT_VIEW_UTILISATION = 'PERMISSION_MPL_ISEMT_DOC_UTILISATION_VIEW',

    DOCUMENT_CREATE_AGGREGATION = 'PERMISSION_MPL_ISEMT_DOC_AGGREGATION_CREATE',
    DOCUMENT_VIEW_AGGREGATION = 'PERMISSION_MPL_ISEMT_DOC_AGGREGATION_VIEW',

    DOCUMENT_CREATE_DROPOUT = 'PERMISSION_MPL_ISEMT_DOC_DROPOUT_CREATE',
    DOCUMENT_VIEW_DROPOUT = 'PERMISSION_MPL_ISEMT_DOC_DROPOUT_VIEW',

    DOCUMENT_CREATE_COMMISSIONING = 'PERMISSION_MPL_ISEMT_DOC_COMMISSIONING_CREATE',
    DOCUMENT_VIEW_COMMISSIONING = 'PERMISSION_MPL_ISEMT_DOC_COMMISSIONING_VIEW',

    CONSUMER_RETURN_VIEW = 'PERMISSION_MPL_CONSUMER_RETURN_VIEW',
    CONSUMER_RETURN_CREATE = 'PERMISSION_MPL_CONSUMER_RETURN_CREATE',

    // GTIN dictionary
    GTIN_GRID_VIEW = 'PERMISSION_MPL_DICT_PRODUCT_CARDS_GRID_VIEW',
    ALL_GTIN_GRID_VIEW = 'PERMISSION_MPL_ISEMT_PRODUCT_ALL_VIEW',
    GTIN_DETAIL_VIEW = 'PERMISSION_MPL_DICT_PRODUCT_CARDS_CARD_VIEW',
    GTIN_CREATE = 'PERMISSION_MPL_DICT_PRODUCT_CARDS_LOAD',
    INTERNAL_CARD_CREATE = 'PERMISSION_MPL_INTERNAL_CARD_CREATE',

    // Virtual warehouse
    VIRTUAL_WAREHOUSE_VIEW = 'PERMISSION_MPL_PRODUCT_STORE_GRID_VIEW',

    // Operator
    PERMISSION_MPL_OPERATOR_ADDITIONAL_TABLES_DATA = 'PERMISSION_MPL_OPERATOR_ADDITIONAL_TABLES_DATA',

    // Invoices
    INVOICE_TRANSFER_VIEW = 'PERMISSION_MPL_ISEMT_INVOICE_TRANSFER_VIEW',

    // Blocking
    MPL_BLOCKING_CREATE = 'PERMISSION_MPL_BLOCKING_CREATE',
    MPL_BLOCKING_CLOSE = 'PERMISSION_MPL_BLOCKING_CLOSE',
    MPL_BLOCKING_VIEW = 'PERMISSION_MPL_BLOCKING_VIEW',
    MPL_BLOCKING_LIST_VIEW = 'PERMISSION_MPL_BLOCKING_LIST_VIEW',

    // Documents declaration
    DOCUMENTS_DECLARATION_LIST = 'PERMISSION_MPL_ISEMT_DOCUMENTS_DECLARATION_LIST',
    DOCUMENTS_DECLARATION_DETAILS = 'PERMISSION_MPL_ISEMT_DOCUMENTS_DECLARATION_DETAILS',
    DOCUMENTS_DECLARATION_CREATE = 'PERMISSION_MPL_ISEMT_DOCUMENTS_DECLARATION_CREATE',
    DOCUMENTS_DECLARATION_EDIT = 'PERMISSION_MPL_ISEMT_DOCUMENTS_DECLARATION_EDIT',
    DOCUMENTS_DECLARATION_CHANGE_TO_SUBMIT_STATUS = 'PERMISSION_MPL_ISEMT_DOCUMENTS_DECLARATION_CHANGE_TO_SUBMIT_STATUS',

    // Customs section
    CUSTOMS_DECLARATION_CONFIRM = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_CONFIRM',
    CUSTOMS_DECLARATION_DOWNLOAD = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_DOWNLOAD',
    CUSTOMS_LIST = 'PERMISSION_MPL_ISEMT_CUSTOMS_LIST',
    CUSTOMS_EDIT = 'PERMISSION_MPL_ISEMT_CUSTOMS_EDIT',
    CUSTOMS_CREATE = 'PERMISSION_MPL_ISEMT_CUSTOMS_CREATE',
    CUSTOMS_EMPLOYEE_LIST = 'PERMISSION_MPL_ISEMT_CUSTOMS_EMPLOYEE_LIST',
    CUSTOMS_EMPLOYEE_DETAILS = 'PERMISSION_MPL_ISEMT_CUSTOMS_EMPLOYEE_DETAILS',
    CUSTOMS_EMPLOYEE_CREATE = 'PERMISSION_MPL_ISEMT_CUSTOMS_EMPLOYEE_CREATE',
    CUSTOMS_EMPLOYEE_EDIT = 'PERMISSION_MPL_ISEMT_CUSTOMS_EMPLOYEE_EDIT',
    CUSTOMS_DECLARATION_LIST = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_LIST',
    CUSTOMS_DECLARATION_DETAILS = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_DETAILS',
    CUSTOMS_DECLARATION_CREATE = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_CREATE',
    CUSTOMS_DECLARATION_EDIT = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_EDIT',
    CUSTOMS_DECLARATION_CHANGE_TO_SUBMIT_STATUS = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_CHANGE_TO_SUBMIT_STATUS',
    CUSTOMS_DECLARATION_CHANGE_TO_REVIEW_STATUS = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_CHANGE_TO_REVIEW_STATUS',
    CUSTOMS_DECLARATION_CONFIRM_OR_REJECT = 'PERMISSION_MPL_ISEMT_CUSTOMS_DECLARATION_CONFIRM_OR_REJECT',
}

export type UserPermissionValue = UserPermission | (string & {});

export enum UserRoles {
    ROLE_MPL_ADMIN = 'ROLE_MPL_ADMIN',
    ROLE_MPL_OPERATOR_ADMIN = 'ROLE_MPL_OPERATOR_ADMIN',
    ROLE_MPL_OPERATOR = 'ROLE_MPL_OPERATOR',
    ROLE_MPL_MANUFACTURER_DIRECTOR = 'ROLE_MPL_MANUFACTURER_DIRECTOR',
    ROLE_MPL_IMPORTER_DIRECTOR = 'ROLE_MPL_IMPORTER_DIRECTOR',
    ROLE_MPL_DISTRIBUTOR_DIRECTOR = 'ROLE_MPL_DISTRIBUTOR_DIRECTOR',
    ROLE_MPL_RETAIL_DIRECTOR = 'ROLE_MPL_RETAIL_DIRECTOR',
    ROLE_MPL_MANUFACTURER_EMPLOYEE = 'ROLE_MPL_MANUFACTURER_EMPLOYEE',
    ROLE_MPL_IMPORTER_EMPLOYEE = 'ROLE_MPL_IMPORTER_EMPLOYEE',
    ROLE_MPL_DISTRIBUTOR_EMPLOYEE = 'ROLE_MPL_DISTRIBUTOR_EMPLOYEE',
    ROLE_MPL_RETAIL_EMPLOYEE = 'ROLE_MPL_RETAIL_EMPLOYEE',
    ROLE_MPL_NK_DIRECTOR = 'ROLE_MPL_NK_DIRECTOR',
    ROLE_MPL_NK_ADMINISTRATOR = 'ROLE_MPL_NK_ADMINISTRATOR',
    ROLE_MPL_NK_EMPLOYEE = 'ROLE_MPL_NK_EMPLOYEE',
    ROLE_MPL_TS_DIRECTOR = 'ROLE_MPL_TS_DIRECTOR',
    ROLE_MPL_TS_EMPLOYEE = 'ROLE_MPL_TS_EMPLOYEE',
}

export type DirectorRegistrationRequest = {
    role: ProfileRole;
    type: ProfileType.DIRECTOR;
    email: string;
    password?: string;
    password_confirm?: string;
    phone: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    contact_email?: string;
    legal_person: LegalPerson;
};

export interface DirectorRegistrationWithCertRequest {
    role: ProfileRole;
    type: ProfileType.EDS_DIRECTOR;
    email: string;
    password: string;
    password_confirm?: string;
    phone: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    legal_person: Partial<LegalPerson>;
    tin?: string;
    thumbprint: string;
}

export interface EmployeeRegistrationRequest {
    type: ProfileType.EMPLOYEE;
    email: string;
    password?: string;
    password_confirm?: string;
    phone?: string;
    additional_phone?: string;
    first_name: string;
    middle_name?: string;
    last_name: string;
    contact_email?: string;
    personal_tin?: string;
}

export interface EmployeeUpdateRequest {
    phone: {value: string; reset: boolean};
    firstName: {value: string; reset: boolean};
    middleName: {value: string; reset: boolean};
    lastName: {value: string; reset: boolean};
}

export interface ParticipantRegistrationRequest {
    userIds: number[];
    gcp: string;
    businessPlaceCode: string;
    storageTypeId: number;
    storageGln: string;
    storageName: string;
    storageAddressId: number;
    storageAddress?: string;
}

export interface ParticipantUpdateRequest {
    gln: {value: string; reset: boolean};
    storageTypeId: {value: number; reset: boolean};
    storageName: {value: string; reset: boolean};
}
